/**
 * CyclesTab — operational home of mesocycles on ClientDetailPage.
 *
 * UX principles (from product direction):
 *   * Chronological list, active cycle pinned and visually distinct
 *   * Calm operational tone — NOT analytics (no charts, no KPI tiles)
 *   * Per-row: focus chip, dates, weeks (planned vs elapsed), note, status
 *   * Inline actions: Edit, Close, Delete (close is a confirm-step)
 *   * "+ New cycle" creates and auto-closes any prior active cycle
 *
 * No metrics in this tab — the per-cycle snapshot lives in Phase 2B's
 * `services/cycle_metrics.py` and surfaces in the Metrics tab (Phase 3).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FOCUS_LABELS, formatWeekN, daysSince } from './useCycles';
import { CycleCreateModal } from './CycleCreateModal';
import { CycleEditModal } from './CycleEditModal';
import { CycleCloseModal } from './CycleCloseModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOCUS_CHIP = {
  hypertrophy: 'bg-purple-500/20 text-purple-200 border-purple-400/40',
  strength:    'bg-rose-500/20 text-rose-200 border-rose-400/40',
  maintenance: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40',
  rehab:       'bg-sky-500/20 text-sky-200 border-sky-400/40',
  deload:      'bg-amber-500/20 text-amber-200 border-amber-400/40',
  general:     'bg-slate-500/20 text-slate-200 border-slate-400/40',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso + (iso.length === 10 ? 'T00:00:00Z' : ''));
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const elapsedWeeks = (cycle) => {
  const days = daysSince(cycle.start_date);
  if (days == null || days < 0) return 0;
  if (!cycle.active && cycle.end_date) {
    const span = Math.max(
      0,
      Math.floor((new Date(cycle.end_date + 'T00:00:00Z') - new Date(cycle.start_date + 'T00:00:00Z')) / (1000 * 60 * 60 * 24))
    );
    return Math.max(1, Math.ceil(span / 7));
  }
  return Math.max(1, Math.floor(days / 7) + 1);
};

const CycleRow = ({ cycle, onEdit, onCloseRequest, onDelete }) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const chip = FOCUS_CHIP[cycle.focus] || FOCUS_CHIP.general;
  const week = formatWeekN(cycle);
  const elapsed = elapsedWeeks(cycle);

  return (
    <div
      className={`rounded-md border px-4 py-3 ${
        cycle.active
          ? 'border-emerald-500/40 bg-emerald-500/[0.04]'
          : 'border-slate-700/60 bg-slate-900/30'
      }`}
      data-testid={`cycle-row-${cycle.id}`}
    >
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        {/* Status pip */}
        <div className="pt-1 w-3">
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              cycle.active ? 'bg-emerald-400' : 'bg-slate-500'
            }`}
            title={cycle.active ? 'Active' : 'Closed'}
          />
        </div>

        {/* Focus + note */}
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded border ${chip}`} data-testid={`cycle-focus-chip-${cycle.id}`}>
              {FOCUS_LABELS[cycle.focus] || cycle.focus}
            </span>
            {cycle.active && (
              <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">
                Active
              </span>
            )}
          </div>
          {cycle.focus_note && (
            <div className="text-slate-300 text-sm mt-1 italic">{cycle.focus_note}</div>
          )}
        </div>

        {/* Dates */}
        <div className="min-w-[180px] text-sm">
          <div className="text-slate-400 text-[11px] uppercase tracking-wider">Dates</div>
          <div className="text-slate-200" data-testid={`cycle-dates-${cycle.id}`}>
            {fmtDate(cycle.start_date)} <span className="text-slate-500">→</span>{' '}
            {cycle.end_date ? fmtDate(cycle.end_date) : <span className="text-slate-500">today</span>}
          </div>
        </div>

        {/* Week */}
        <div className="min-w-[100px] text-sm">
          <div className="text-slate-400 text-[11px] uppercase tracking-wider">Week</div>
          <div className="text-slate-200" data-testid={`cycle-week-${cycle.id}`}>
            {cycle.active ? week : (
              cycle.expected_weeks
                ? `${elapsed}/${cycle.expected_weeks}`
                : `${elapsed}w`
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="ml-auto flex items-center gap-2">
          {confirmingDelete ? (
            <>
              <span className="text-slate-300 text-xs">Delete this cycle?</span>
              <button
                onClick={() => { onDelete(); setConfirmingDelete(false); }}
                className="text-xs px-2 py-1 rounded bg-rose-600/80 hover:bg-rose-500 text-white"
                data-testid={`cycle-confirm-delete-${cycle.id}`}
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                className="text-xs px-2 py-1 rounded text-slate-300 hover:text-white"
                data-testid={`cycle-cancel-delete-${cycle.id}`}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onEdit}
                className="text-xs px-2 py-1 rounded text-slate-300 hover:text-white hover:bg-slate-800"
                data-testid={`cycle-edit-btn-${cycle.id}`}
              >
                Edit
              </button>
              {cycle.active && (
                <button
                  onClick={onCloseRequest}
                  className="text-xs px-2 py-1 rounded text-emerald-300 hover:text-emerald-200 hover:bg-emerald-900/30"
                  data-testid={`cycle-close-btn-${cycle.id}`}
                >
                  Close & report
                </button>
              )}
              <button
                onClick={() => setConfirmingDelete(true)}
                className="text-xs px-2 py-1 rounded text-rose-300 hover:text-rose-200 hover:bg-rose-900/30"
                data-testid={`cycle-delete-btn-${cycle.id}`}
                title="Permanently delete (for mistakes — normal flow is Close)"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const CyclesTab = ({ clientId, onChange, autoAction }) => {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);
  const autoActionDone = useRef(false);

  const refresh = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/cycles`, { params: { client_id: clientId } });
      setCycles(r.data.items || []);
    } catch (e) {
      console.error('[CyclesTab] failed to load', e);
      setError('Could not load cycles');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Deep-link from Hoy: auto-open the close modal for the active cycle once.
  useEffect(() => {
    if (autoActionDone.current || loading) return;
    if (autoAction === 'close') {
      const activeCycle = cycles.find((c) => c.active);
      if (activeCycle) {
        autoActionDone.current = true;
        setCloseTarget(activeCycle);
      }
    }
  }, [autoAction, loading, cycles]);

  // Notify parent (ClientDetailPage) so the header CycleContextBlock refreshes.
  const notifyChange = () => { if (onChange) onChange(); };

  const handleCreate = async (payload) => {
    await axios.post(`${API}/cycles`, { client_id: clientId, ...payload });
    await refresh();
    notifyChange();
  };

  const handleSaveEdit = async (updates) => {
    if (!editTarget) return;
    await axios.patch(`${API}/cycles/${editTarget.id}`, updates);
    await refresh();
    notifyChange();
  };

  const handleClose = async (cycleId, payload) => {
    await axios.post(`${API}/cycles/${cycleId}/close`, payload || {});
    await refresh();
    notifyChange();
  };

  const handleDelete = async (cycleId) => {
    await axios.delete(`${API}/cycles/${cycleId}`);
    await refresh();
    notifyChange();
  };

  // Active cycle pinned at top; closed cycles below, most recent first
  const active = cycles.filter((c) => c.active);
  const closed = cycles.filter((c) => !c.active);

  return (
    <div className="space-y-4" data-testid="cycles-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white text-lg font-semibold">Cycles</h3>
          <p className="text-slate-400 text-sm">
            Operational training blocks. Trainer-authored — never auto-derived.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium"
          data-testid="cycles-tab-create-btn"
        >
          + New cycle
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading cycles…</div>
      ) : error ? (
        <div className="text-red-300 text-sm">{error}</div>
      ) : cycles.length === 0 ? (
        <div
          className="border border-dashed border-slate-700 rounded-md px-6 py-8 text-center"
          data-testid="cycles-tab-empty"
        >
          <div className="text-slate-300 text-sm">No cycles yet for this client.</div>
          <div className="text-slate-500 text-xs mt-1">
            Declare a training block to give the work an operational shape.
          </div>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <div className="space-y-2">
              {active.map((c) => (
                <CycleRow
                  key={c.id}
                  cycle={c}
                  onEdit={() => setEditTarget(c)}
                  onCloseRequest={() => setCloseTarget(c)}
                  onDelete={() => handleDelete(c.id)}
                />
              ))}
            </div>
          )}

          {closed.length > 0 && (
            <div className="pt-3">
              <div className="text-slate-400 text-[11px] uppercase tracking-wider mb-2">
                History ({closed.length})
              </div>
              <div className="space-y-2">
                {closed.map((c) => (
                  <CycleRow
                    key={c.id}
                    cycle={c}
                    onEdit={() => setEditTarget(c)}
                    onCloseRequest={() => setCloseTarget(c)}
                    onDelete={() => handleDelete(c.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <CycleCreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
      />
      <CycleEditModal
        open={!!editTarget}
        cycle={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={handleSaveEdit}
      />
      <CycleCloseModal
        open={!!closeTarget}
        cycle={closeTarget}
        onClose={() => setCloseTarget(null)}
        onConfirm={(payload) => handleClose(closeTarget.id, payload)}
      />
    </div>
  );
};

export default CyclesTab;
